import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const PaymentStatusNotification = ({ 
  paymentStatus = null,
  onDismiss,
  onViewDetails,
  className = '' 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    // Simulate payment status updates
    if (paymentStatus) {
      const notification = {
        id: Date.now(),
        type: paymentStatus?.type || 'pending',
        title: getStatusTitle(paymentStatus?.type),
        message: paymentStatus?.message || getStatusMessage(paymentStatus?.type),
        amount: paymentStatus?.amount,
        ticketNumber: paymentStatus?.ticketNumber,
        timestamp: new Date(),
        autoHide: paymentStatus?.type === 'success'
      };

      setNotifications(prev => [notification, ...prev?.slice(0, 2)]);
      setIsVisible(true);

      // Auto-hide success notifications after 5 seconds
      if (notification?.autoHide) {
        setTimeout(() => {
          handleDismiss(notification?.id);
        }, 5000);
      }
    }
  }, [paymentStatus]);

  const getStatusTitle = (type) => {
    switch (type) {
      case 'success': return '¡Pago Exitoso!';
      case 'pending': return 'Pago Pendiente';
      case 'processing': return 'Procesando Pago';
      case 'failed': return 'Pago Fallido';
      case 'refund': return 'Reembolso Procesado';
      default: return 'Actualización de Pago';
    }
  };

  const getStatusMessage = (type) => {
    switch (type) {
      case 'success': return 'Tu pago ha sido confirmado y tu boleto está listo';
      case 'pending': return 'Estamos verificando tu pago, recibirás confirmación pronto';
      case 'processing': return 'Tu pago está siendo procesado por el banco';
      case 'failed': return 'Hubo un problema con tu pago, intenta nuevamente';
      case 'refund': return 'Tu reembolso ha sido procesado exitosamente';
      default: return 'Hay una actualización sobre tu pago';
    }
  };

  const getStatusIcon = (type) => {
    switch (type) {
      case 'success': return 'CheckCircle';
      case 'pending': return 'Clock';
      case 'processing': return 'RefreshCw';
      case 'failed': return 'XCircle';
      case 'refund': return 'RotateCcw';
      default: return 'Info';
    }
  };

  const getStatusColor = (type) => {
    switch (type) {
      case 'success': return 'text-success bg-success/10 border-success/20';
      case 'pending': return 'text-warning bg-warning/10 border-warning/20';
      case 'processing': return 'text-secondary bg-secondary/10 border-secondary/20';
      case 'failed': return 'text-error bg-error/10 border-error/20';
      case 'refund': return 'text-accent bg-accent/10 border-accent/20';
      default: return 'text-foreground bg-muted/10 border-border';
    }
  };

  const handleDismiss = (notificationId) => {
    setNotifications(prev => prev?.filter(n => n?.id !== notificationId));
    if (notifications?.length <= 1) {
      setIsVisible(false);
    }
    onDismiss?.(notificationId);
  };

  const handleViewDetails = (notification) => {
    onViewDetails?.(notification);
    // Navigate to appropriate page based on status
    if (notification?.type === 'success' && notification?.ticketNumber) {
      window.location.href = '/ticket-verification';
    } else {
      window.location.href = '/payment-confirmation';
    }
  };

  if (!isVisible || notifications?.length === 0) return null;

  return (
    <div className={`fixed top-20 right-6 z-90 space-y-3 ${className}`}>
      {notifications?.map((notification) => (
        <div
          key={notification?.id}
          className={`bg-card rounded-xl shadow-luxury border p-4 w-80 max-w-[calc(100vw-3rem)] animate-slide-in-from-right ${
            getStatusColor(notification?.type)
          }`}
        >
          <div className="flex items-start space-x-3">
            <div className={`p-2 rounded-lg ${getStatusColor(notification?.type)?.replace('text-', 'bg-')?.replace('bg-', 'bg-')?.replace('/10', '/20')}`}>
              <Icon 
                name={getStatusIcon(notification?.type)} 
                size={16} 
                className={getStatusColor(notification?.type)?.split(' ')?.[0]}
              />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <h3 className="font-body font-semibold text-card-foreground">
                  {notification?.title}
                </h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDismiss(notification?.id)}
                  className="h-6 w-6 -mt-1 -mr-1"
                >
                  <Icon name="X" size={12} />
                </Button>
              </div>
              
              <p className="text-sm text-muted-foreground mt-1 font-caption">
                {notification?.message}
              </p>

              {/* Payment Details */}
              {(notification?.amount || notification?.ticketNumber) && (
                <div className="mt-3 p-2 bg-muted/30 rounded-lg">
                  {notification?.amount && (
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Monto:</span>
                      <span className="font-mono font-medium text-card-foreground">
                        Bs. {notification?.amount?.toLocaleString()}
                      </span>
                    </div>
                  )}
                  {notification?.ticketNumber && (
                    <div className="flex justify-between text-xs mt-1">
                      <span className="text-muted-foreground">Boleto:</span>
                      <span className="font-mono font-medium text-card-foreground">
                        {notification?.ticketNumber}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center space-x-2 mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewDetails(notification)}
                  iconName="ExternalLink"
                  iconSize={12}
                  className="text-xs"
                >
                  Ver Detalles
                </Button>
                
                {notification?.type === 'failed' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => window.location.href = '/purchase-flow'}
                    iconName="RefreshCw"
                    iconSize={12}
                    className="text-xs"
                  >
                    Reintentar
                  </Button>
                )}
              </div>

              {/* Timestamp */}
              <div className="flex items-center justify-between mt-3 pt-2 border-t border-border">
                <span className="text-xs text-muted-foreground font-caption">
                  {notification?.timestamp?.toLocaleTimeString('es-VE', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
                
                {notification?.type === 'processing' && (
                  <div className="flex items-center space-x-1">
                    <div className="w-1 h-1 bg-secondary rounded-full animate-golden-pulse"></div>
                    <span className="text-xs text-secondary font-caption">En proceso</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PaymentStatusNotification;